import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { procedureAPI } from './services/api';
import { styles, globalCSS, STATUS_CONFIG } from './styles';
import { Icons } from './components/Icons';
import PatientCard from './components/PatientCard';
import AddPatientForm from './components/AddPatientForm';
import FilterToolbar from './components/FilterToolbar';

/**
 * THU THUAT SCHEDULER - Main App
 * Medical procedure scheduling dashboard
 */
function App() {
  // ─── State ───────────────────────────────────────────
  const [patients, setPatients] = useState([]);
  const [procedureTypes, setProcedureTypes] = useState([]);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const searchRef = useRef(null);

  // ─── Toast Helper ────────────────────────────────────
  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2400);
  }, []);

  // ─── Data Transform: Flat appointments → Grouped patients ───
  const transformScheduleToPatients = useCallback((schedule) => {
    if (!schedule || schedule.length === 0) return [];

    // Group appointments by patient name
    const grouped = {};
    schedule.forEach(appt => {
      if (!grouped[appt.patient_name]) {
        grouped[appt.patient_name] = {
          id: appt.patient_id,
          name: appt.patient_name,
          procedures: [],
        };
      }
      grouped[appt.patient_name].procedures.push({
        id: appt.id,
        procedure_type: appt.procedure_name,
        start_time: appt.start_time,
        end_time: appt.end_time,
        status: appt.status?.toUpperCase() || 'PENDING',
      });
    });

    // Derive patient status from procedures
    return Object.values(grouped).map(patient => {
      const statuses = patient.procedures.map(p => p.status);
      let status = 'PENDING';

      if (statuses.every(s => s === 'COMPLETED')) {
        status = 'COMPLETED';
      } else if (statuses.some(s => s === 'IN_PROGRESS')) {
        status = 'IN_PROGRESS';
      }

      return { ...patient, status };
    });
  }, []);

  // ─── API: Load Data ──────────────────────────────────
  const loadProcedureTypes = useCallback(async () => {
    try {
      const response = await procedureAPI.getProcedureTypes();
      // Transform to match component expectations
      const types = response.data.map(p => ({
        id: p.id,
        name: p.name,
        duration_minutes: p.duration,
      }));
      setProcedureTypes(types);
    } catch (err) {
      console.error('Error loading procedure types:', err);
    }
  }, []);

  const loadSchedule = useCallback(async () => {
    try {
      const response = await procedureAPI.getSchedule(selectedDate);
      const schedule = response.data.schedule || [];
      const transformed = transformScheduleToPatients(schedule);
      setPatients(transformed);
    } catch (err) {
      console.error('Error loading schedule:', err);
    }
  }, [selectedDate, transformScheduleToPatients]);

  // ─── Effects: Load on mount & polling ────────────────
  useEffect(() => {
    loadProcedureTypes();
  }, [loadProcedureTypes]);

  useEffect(() => {
    loadSchedule();

    // Polling every 10 seconds
    const interval = setInterval(loadSchedule, 10000);
    return () => clearInterval(interval);
  }, [loadSchedule]);

  // ─── Keyboard Shortcuts ──────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      // Don't handle when in input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        if (e.key === 'Escape') {
          e.target.blur();
          setSearchQuery('');
        }
        return;
      }

      // N = toggle add panel
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        setShowAddPanel(prev => !prev);
      }

      // / = focus search
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // ─── API: Add Patient ────────────────────────────────
  const handleAdd = async (data) => {
    setLoading(true);
    try {
      const startTime = data.startTime;

      await procedureAPI.createSchedule({
        date: selectedDate,
        start_time: startTime,
        patients: [{
          patient_name: data.name,
          procedure_ids: data.procedures.map(p => p.id),
        }],
      });

      await loadSchedule();
      setShowAddPanel(false);
      showToast(`Đã thêm ${data.name}`);
    } catch (err) {
      console.error('Error adding patient:', err);
      showToast('Có lỗi xảy ra', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ─── API: Update Status ──────────────────────────────
  const handleStatusChange = async (patientId, newStatus) => {
    try {
      const patient = patients.find(p => p.id === patientId);
      if (!patient) return;

      await procedureAPI.updatePatientStatus(
        patient.name,
        selectedDate,
        newStatus.toLowerCase()
      );

      await loadSchedule();
      const label = STATUS_CONFIG[newStatus]?.label || newStatus;
      showToast(`Trạng thái → ${label}`);
    } catch (err) {
      console.error('Error updating status:', err);
      showToast('Có lỗi xảy ra', 'error');
    }
  };

  // ─── API: Delete Patient ─────────────────────────────
  const handleDelete = async (patientId) => {
    try {
      const patient = patients.find(p => p.id === patientId);
      if (!patient) return;

      await procedureAPI.deletePatientAppointments(patient.name, selectedDate);
      await loadSchedule();
      setDeleteConfirm(null);
      showToast('Đã xoá bệnh nhân', 'info');
    } catch (err) {
      console.error('Error deleting:', err);
      showToast('Có lỗi xảy ra', 'error');
    }
  };

  // ─── Computed: Stats ─────────────────────────────────
  const stats = useMemo(() => {
    const total = patients.length;
    const completed = patients.filter(p => p.status === 'COMPLETED').length;
    const inProgress = patients.filter(p => p.status === 'IN_PROGRESS').length;
    const pending = patients.filter(p => p.status === 'PENDING').length;
    const totalProcs = patients.reduce((s, p) => s + p.procedures.length, 0);
    const doneProcs = patients.reduce(
      (s, p) => s + p.procedures.filter(pr => pr.status === 'COMPLETED').length,
      0
    );
    const pct = totalProcs === 0 ? 0 : Math.round((doneProcs / totalProcs) * 100);
    return { total, completed, inProgress, pending, totalProcs, doneProcs, pct };
  }, [patients]);

  // ─── Computed: Filtered Patients ─────────────────────
  const filteredPatients = useMemo(() => {
    let list = patients;

    if (statusFilter !== 'ALL') {
      list = list.filter(p => p.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q));
    }

    return list;
  }, [patients, statusFilter, searchQuery]);

  // ─── Helpers ─────────────────────────────────────────
  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return new Intl.DateTimeFormat('vi-VN', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(d);
  };

  // ─── Render ──────────────────────────────────────────
  return (
    <div style={styles.root}>
      <style>{globalCSS}</style>

      {/* ── Header ── */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.headerLeft}>
            <div style={styles.logo}>
              {Icons.eye({ size: 22, color: '#1e40af' })}
              <span style={styles.logoText}>Thủ Thuật</span>
            </div>
            <div style={styles.dateBadge}>
              {Icons.calendar({ size: 14, color: '#64748b' })}
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={styles.dateInput}
              />
            </div>
          </div>
          <div style={styles.headerRight}>
            <div style={styles.statsChips}>
              <span style={{ ...styles.chip, background: '#f0fdf4', color: '#16a34a' }}>
                {stats.completed} xong
              </span>
              <span style={{ ...styles.chip, background: '#eff6ff', color: '#2563eb' }}>
                {stats.inProgress} đang
              </span>
              <span style={{ ...styles.chip, background: '#f1f5f9', color: '#64748b' }}>
                {stats.pending} chờ
              </span>
            </div>
            <button
              onClick={() => setShowAddPanel(!showAddPanel)}
              style={showAddPanel ? styles.btnClose : styles.btnAdd}
            >
              {showAddPanel
                ? Icons.x({ size: 18, color: '#64748b' })
                : Icons.plus({ size: 18, color: '#fff' })
              }
              <span style={{ fontSize: 13, fontWeight: 700 }}>
                {showAddPanel ? 'Đóng' : 'Thêm BN'}
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* ── Add Panel ── */}
      {showAddPanel && (
        <div style={styles.addPanelWrap}>
          <AddPatientForm
            procedures={procedureTypes}
            onSubmit={handleAdd}
            onClose={() => setShowAddPanel(false)}
          />
        </div>
      )}

      {/* ── Main Content ── */}
      <main style={styles.main}>
        <div style={styles.container}>
          {/* Filter Toolbar */}
          <FilterToolbar
            stats={stats}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            searchRef={searchRef}
          />

          {/* Date Label */}
          <div style={styles.sectionHeader}>
            <span style={styles.sectionLabel}>{formatDate(selectedDate)}</span>
            <div style={styles.sectionLine} />
            {filteredPatients.length !== patients.length && (
              <span style={styles.filterCount}>
                {filteredPatients.length}/{patients.length}
              </span>
            )}
          </div>

          {/* Patient List */}
          {filteredPatients.length === 0 ? (
            <div style={styles.emptyState}>
              {searchQuery || statusFilter !== 'ALL'
                ? Icons.search({ size: 48, color: '#cbd5e1' })
                : Icons.user({ size: 48, color: '#cbd5e1' })
              }
              <p style={{ margin: '12px 0 4px', fontWeight: 600, color: '#94a3b8' }}>
                {searchQuery || statusFilter !== 'ALL'
                  ? 'Không tìm thấy bệnh nhân'
                  : 'Chưa có bệnh nhân'
                }
              </p>
              <p style={{ fontSize: 13, color: '#cbd5e1' }}>
                {searchQuery || statusFilter !== 'ALL'
                  ? 'Thử thay đổi bộ lọc hoặc từ khoá'
                  : <>Nhấn <kbd style={styles.kbd}>N</kbd> để thêm mới</>
                }
              </p>
            </div>
          ) : (
            <div style={styles.patientList}>
              {filteredPatients.map((p, idx) => (
                <PatientCard
                  key={p.id}
                  data={p}
                  index={idx}
                  expanded={expandedId === p.id || p.status === 'IN_PROGRESS'}
                  onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
                  onStatusChange={handleStatusChange}
                  onDelete={() => setDeleteConfirm(p.id)}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* ── Footer Progress ── */}
      <footer style={styles.footer}>
        <div style={styles.footerInner}>
          <div style={styles.footerLeft}>
            {Icons.barChart({ size: 16, color: '#64748b' })}
            <span style={styles.footerText}>
              {stats.doneProcs}/{stats.totalProcs} thủ thuật
            </span>
          </div>
          <div style={styles.progressWrap}>
            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressBar, width: `${stats.pct}%` }} />
            </div>
            <span style={styles.progressLabel}>{stats.pct}%</span>
          </div>
        </div>
      </footer>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          ...styles.toast,
          background: toast.type === 'info' ? '#1e293b' : toast.type === 'error' ? '#ef4444' : '#16a34a'
        }}>
          {toast.type === 'success'
            ? Icons.check({ size: 16, color: '#fff' })
            : Icons.trash({ size: 16, color: '#fff' })
          }
          <span>{toast.msg}</span>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteConfirm && (
        <div style={styles.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={styles.confirmBox} onClick={e => e.stopPropagation()}>
            <p style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, color: '#0f172a' }}>
              Xoá bệnh nhân?
            </p>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
              Thao tác này không thể hoàn tác.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={styles.btnCancel}>
                Huỷ
              </button>
              <button onClick={() => handleDelete(deleteConfirm)} style={styles.btnDanger}>
                Xoá
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 60,
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#1e40af',
          color: '#fff',
          padding: '6px 16px',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          {Icons.loader({ size: 14, color: '#fff' })}
          <span>Đang xử lý...</span>
        </div>
      )}
    </div>
  );
}

export default App;
